<template>
  <div class="login-view">
    <div class="login-container">
      <h2 class="title">欢迎回来</h2>
      <p class="subtitle">登录您的AiVitaHelper账号</p>      
      <el-form 
        ref="loginFormRef"
        :model="loginForm" 
        :rules="rules" 
        class="login-form"
      >
        <el-form-item prop="identifier">
          <el-input
            v-model="loginForm.identifier"
            placeholder="账号/邮箱"
            :prefix-icon="User"
          />
        </el-form-item>
        
        <el-form-item prop="password">
          <el-input
            v-model="loginForm.password"
            type="password"
            placeholder="密码"
            :prefix-icon="Lock"
            show-password
          />
        </el-form-item>
        
        <div class="form-options">
          <el-checkbox v-model="loginForm.rememberMe">记住我</el-checkbox>
          <div class="auth-links">
            <router-link to="/forgot-password" class="auth-link">
              忘记密码？
            </router-link>
            <router-link to="/restore-account" class="auth-link">
              恢复账号
            </router-link>
          </div>
        </div>
        
        <el-button type="primary" class="submit-btn" :loading="loading" @click="handleLogin">
          登录
        </el-button>
        
        <div class="links">
          <router-link to="/register" class="register-link">创建账号</router-link>
        </div>
      </el-form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, ref } from 'vue';
import { User, Lock } from '@element-plus/icons-vue';
import { useAuthStore } from '@/store/modules/auth';
import { ElMessage } from 'element-plus';
import type { FormInstance } from 'element-plus';

export default defineComponent({
  name: 'LoginView',
  setup() {
    const authStore = useAuthStore();
    const loading = ref(false);
    const loginFormRef = ref<FormInstance>();
    
    const loginForm = reactive({
      identifier: '',
      password: '',
      rememberMe: false
    });
    
    const rules = {
      identifier: [
        { required: true, message: '请输入账号或邮箱', trigger: 'blur' }
      ],
      password: [
        { required: true, message: '请输入密码', trigger: 'blur' },
        { min: 6, message: '密码长度不能小于6位', trigger: 'blur' }
      ]
    };
    
    const handleLogin = async () => {
      if (!loginFormRef.value) return;
      
      await loginFormRef.value.validate(async (valid) => {
        if (valid) {
          try {
            loading.value = true;
            await authStore.login({
              identifier: loginForm.identifier,
              password: loginForm.password,
              rememberMe: loginForm.rememberMe
            });
            ElMessage.success('登录成功');
          } catch (error: any) {
            ElMessage.error(error.message || '登录失败');
          } finally {
            loading.value = false;
          }
        }
      });
    };
    
    return {
      loginForm,
      loginFormRef,
      rules,
      loading,
      handleLogin,
      User,
      Lock
    };
  }
});
</script>

<style lang="scss" scoped>
.login-view {
  min-height: calc(100vh - 85px - 60px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background-color: #f5f5f7;
}

.login-container {
  width: 100%;
  max-width: 400px;
  padding: 40px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.title {
  font-size: 1.8rem;
  font-weight: 600;
  color: #222;
  margin-bottom: 8px;
  text-align: center;
}

.subtitle {
  font-size: 1rem;
  color: #666;
  margin-bottom: 32px;
  text-align: center;
}

.login-form {
  .el-input {
    --el-input-height: 44px;
    margin-bottom: 16px;
  }
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  
  .auth-links {
    display: flex;
    gap: 16px;
    
    .auth-link {
      color: #666;
      font-size: 14px;
      text-decoration: none;
      
      &:hover {
        color: #333;
        text-decoration: underline;
      }
    }
  }
}

.submit-btn {
  width: 100%;
  height: 44px;
  font-size: 1rem;
  border-radius: 22px;
  margin-bottom: 20px;
}

.links {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
  
  .register-link {
    color: var(--el-color-primary);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

// 添加移动端适配
@media (max-width: 768px) {
  .login-view {
    min-height: calc(100vh - 60px - 50px);
    padding: 20px 15px;
  }
  
  .login-container {
    padding: 25px 20px;
    max-width: 100%;
  }
  
  .title {
    font-size: 1.5rem;
  }
  
  .subtitle {
    font-size: 0.9rem;
    margin-bottom: 24px;
  }
  
  .form-options {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    .auth-links {
      width: 100%;
      justify-content: space-between;
    }
  }
  
  .submit-btn {
    height: 40px;
    margin-top: 10px;
  }
}
</style> 